import * as THREE from 'three';
import { type ShotStats } from '@/objects/golfBall';
import { UnitConversions } from '@/utils/units';
import { colors } from '@/utils/colors';

export type GroundStatsHUDOptions = {
  units?: 'imperial' | 'metric';
  /** Distance (m) from the tee to the front row of tiles. */
  frontRadius?: number;
  /** Distance (m) from the tee to the back row of tiles. */
  backRadius?: number;
  /** Tile tilt above the ground plane (radians). ~35° reads well standing. */
  tilt?: number;
};

type FieldDef = {
  id: string;
  label: string;
  value: (stats: ShotStats, shot: OpenGolfSim.Shot | undefined, units: 'imperial' | 'metric') => string;
};

const TILE_W = 0.46;
const TILE_H = 0.30;
const CANVAS_W = 320;
const CANVAS_H = 208;

const deg = (value: number, precision = 1) => `${value.toFixed(precision)}°`;

/** Same value semantics as UIShotData.gridOptions (the desktop DOM grid). */
const distance = (meters: number, units: 'imperial' | 'metric', feetUnderFive = false) => {
  if (units === 'imperial') {
    if (feetUnderFive && meters < 5) return `${Math.round(UnitConversions.metersToFeet(meters))} FT`;
    return `${Math.round(UnitConversions.metersToYards(meters))} YD`;
  }
  return `${Math.round(meters)} M`;
};

const FRONT_FIELDS: FieldDef[] = [
  { id: 'speed', label: 'Ball Speed', value: (_s, shot, u) => shot
      ? (u === 'metric'
        ? `${Math.round(UnitConversions.milesPerHourToMetersPerSecond(shot.ballSpeed))} M/S`
        : `${Math.round(shot.ballSpeed)} MPH`)
      : '—' },
  { id: 'carry', label: 'Carry', value: (s, _shot, u) => distance(s.carry, u) },
  { id: 'total', label: 'Total', value: (s, _shot, u) => distance(s.total, u, true) },
  { id: 'apex', label: 'Apex', value: (s, _shot, u) => u === 'imperial'
      ? `${Math.round(UnitConversions.metersToFeet(s.apex))} FT`
      : `${Math.round(s.apex)} M` },
  { id: 'spin', label: 'Spin Rate', value: (_s, shot) => shot ? `${Math.round(shot.spinSpeed)} RPM` : '—' },
];

const BACK_FIELDS: FieldDef[] = [
  { id: 'vla', label: 'VLA', value: (_s, shot) => shot ? deg(shot.verticalLaunchAngle) : '—' },
  { id: 'hla', label: 'HLA', value: (_s, shot) => shot ? deg(shot.horizontalLaunchAngle) : '—' },
  { id: 'spinAxis', label: 'Spin Axis', value: (_s, shot) => shot ? deg(shot.spinAxis, 0) : '—' },
  { id: 'roll', label: 'Roll', value: (s, _shot, u) => distance(s.roll, u) },
  { id: 'lateral', label: 'Lateral', value: (s, _shot, u) => {
      const side = s.lateral > 0.5 ? ' R' : s.lateral < -0.5 ? ' L' : '';
      return distance(Math.abs(s.lateral), u) + side;
    } },
];

/** Shown only when the launch monitor reports club delivery data. */
const CLUB_FIELDS: FieldDef[] = [
  { id: 'clubSpeed', label: 'Club Speed', value: (_s, shot, u) => shot?.club?.clubSpeed !== undefined
      ? (u === 'metric'
        ? `${Math.round(UnitConversions.milesPerHourToMetersPerSecond(shot.club.clubSpeed))} M/S`
        : `${Math.round(shot.club.clubSpeed)} MPH`)
      : '—' },
  { id: 'attackAngle', label: 'Attack', value: (_s, shot) => shot?.club?.attackAngle !== undefined ? deg(shot.club.attackAngle) : '—' },
  { id: 'clubPath', label: 'Path', value: (_s, shot) => shot?.club?.clubPath !== undefined ? deg(shot.club.clubPath) : '—' },
  { id: 'faceToTarget', label: 'Face', value: (_s, shot) => shot?.club?.faceToTarget !== undefined ? deg(shot.club.faceToTarget) : '—' },
];

type Tile = {
  field: FieldDef;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  texture: THREE.CanvasTexture;
  cacheKey: string;
};

/**
 * The MR shot HUD: stat tiles anchored to the ground in front of the player,
 * arced around the target line between the tee and the portal — the Golf+/
 * Trackman "numbers on the turf" pattern. Spatial UI, not head-locked: the
 * tiles are part of the room, pinned to the anchored tee pose, tilted up
 * toward standing eye height. Mask-exempt so they read over both real floor
 * (inside the passthrough bay) and virtual fairway.
 */
export class GroundStatsHUD {
  object: THREE.Group;
  units: 'imperial' | 'metric';

  #tiles: Tile[] = [];
  #clubRow: THREE.Group;

  constructor(options: GroundStatsHUDOptions = {}) {
    this.units = options.units ?? 'imperial';
    const frontRadius = options.frontRadius ?? 2.0;
    const backRadius = options.backRadius ?? 2.75;
    const tilt = options.tilt ?? 0.61; // ~35°

    this.object = new THREE.Group();
    this.object.name = 'GroundStatsHUD';
    this.object.userData.mrMaskExempt = true;
    this.object.visible = false;

    this.#buildArc(FRONT_FIELDS, frontRadius, THREE.MathUtils.degToRad(15), tilt, this.object);
    this.#buildArc(BACK_FIELDS, backRadius, THREE.MathUtils.degToRad(12.5), tilt, this.object);

    // Club delivery row — hidden until a shot actually carries club data
    this.#clubRow = new THREE.Group();
    this.#clubRow.visible = false;
    this.#buildArc(CLUB_FIELDS, backRadius + 0.65, THREE.MathUtils.degToRad(11), tilt, this.#clubRow);
    this.object.add(this.#clubRow);
  }

  /** Pin the HUD to the anchored tee pose (reference space). */
  placeAtTee(teePosition: THREE.Vector3, yaw: number) {
    this.object.position.copy(teePosition);
    this.object.rotation.y = yaw;
    this.object.visible = true;
  }

  setVisible(visible: boolean) {
    this.object.visible = visible;
  }

  /** Live during flight — each tile redraws only when its text changes. */
  update(stats: ShotStats, shot?: OpenGolfSim.Shot) {
    if (!this.object.visible) return;
    this.#clubRow.visible = !!shot?.club;
    for (const tile of this.#tiles) {
      // Before the first shot everything reads '—', not a wall of zeros
      const value = shot ? tile.field.value(stats, shot, this.units) : '—';
      if (value === tile.cacheKey) continue;
      tile.cacheKey = value;
      this.#drawTile(tile, value);
    }
  }

  #buildArc(fields: FieldDef[], radius: number, spacing: number, tilt: number, parent: THREE.Object3D) {
    const count = fields.length;
    for (let i = 0; i < count; i++) {
      const theta = (i - (count - 1) / 2) * spacing;
      const tile = this.#createTile(fields[i]);

      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(TILE_W, TILE_H),
        new THREE.MeshBasicMaterial({
          map: tile.texture,
          transparent: true,
          alphaTest: 0.05,
          fog: false,
        })
      );
      mesh.userData.mrMaskExempt = true;
      // Local frame: tee at origin, +Z downrange. Face the tee, then recline
      // back so the tile lies on the turf with its top edge tipped up.
      mesh.position.set(Math.sin(theta) * radius, 0.01 + Math.sin(tilt) * TILE_H * 0.5, Math.cos(theta) * radius);
      mesh.rotation.order = 'YXZ';
      mesh.rotation.y = theta + Math.PI;
      mesh.rotation.x = -(Math.PI / 2 - tilt);
      parent.add(mesh);
    }
  }

  #createTile(field: FieldDef): Tile {
    const canvas = document.createElement('canvas');
    canvas.width = CANVAS_W;
    canvas.height = CANVAS_H;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Unable to get 2d context for HUD tile');
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    texture.anisotropy = 8; // tiles are viewed at a steep angle

    const tile: Tile = { field, canvas, ctx, texture, cacheKey: '' };
    this.#tiles.push(tile);
    this.#drawTile(tile, '—');
    return tile;
  }

  #drawTile(tile: Tile, value: string) {
    const { ctx, field } = tile;
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);

    ctx.fillStyle = 'rgba(17, 28, 28, 0.88)';
    ctx.beginPath();
    ctx.roundRect(0, 0, CANVAS_W, CANVAS_H, 22);
    ctx.fill();
    ctx.strokeStyle = 'rgba(33, 212, 141, 0.35)';
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = 'bold 30px Rubik, sans-serif';
    ctx.fillStyle = colors.primary;
    ctx.fillText(field.label.toUpperCase(), CANVAS_W / 2, 42);
    ctx.font = 'bold 58px Rubik, sans-serif';
    ctx.fillStyle = '#ffffff';
    ctx.fillText(value, CANVAS_W / 2, 128);

    tile.texture.needsUpdate = true;
  }
}
